/**
 * Centralized lesson slots fetching utility
 * Fetches available lesson slots from Laravel API via Shopify proxy
 */

window.LessonSlots = {
  /**
   * Build proxy URL for lesson data
   * @param {string} lessonId - The Laravel lesson ID (unleash_id)
   * @param {string} host - The Shopify host (e.g., from request.host)
   * @returns {string} The proxy URL
   */
  buildProxyUrl: function(lessonId, host) {
    return 'https://' + host + '/apps/lab9/proxy/lessons/' + encodeURIComponent(lessonId);
  },

  /**
   * Calculate available slots from lesson data
   * @param {Object} data - The lesson data from API
   * @returns {number} Available slots count
   */
  calculateAvailableSlots: function(data) {
    if (!data) {
      return 0;
    }

    const rawApiCount = data.available_slots_count != null ? data.available_slots_count : data.availableSlotsCount;
    if (rawApiCount !== undefined && rawApiCount !== null && rawApiCount !== '') {
      const parsed = Number(rawApiCount);
      if (Number.isFinite(parsed)) {
        return Math.max(0, parsed);
      }
    }

    const totalSlots = data.slots || 0;
    
    // Handle different possible booking formats
    let bookedSlots = 0;
    if (data.bookings && Array.isArray(data.bookings)) {
      bookedSlots = data.bookings.filter(b => !b.cancelled_at).length;
    } else if (data.bookings_count !== undefined) {
      // If API returns a count directly
      bookedSlots = data.bookings_count;
    }
    
    return Math.max(0, totalSlots - bookedSlots);
  },

  /**
   * Fetch lesson slots and update element
   * @param {string} lessonId - The Laravel lesson ID (unleash_id)
   * @param {string|HTMLElement} targetElement - Element ID or HTMLElement to update
   * @param {Object} options - Optional configuration
   * @param {string} options.host - Shopify host (defaults to window.location.host)
   * @param {string} options.loadingText - Text to show while loading (default: from translations or 'Loading...')
   * @param {string} options.errorText - Text to show on error (default: from translations or 'Error loading')
   * @param {string} options.unavailableText - Text to show when unavailable (default: from translations or 'Not available')
   * @param {string} options.volzetText - Text to show when full (default: from translations or 'Full')
   * @param {Function} options.onUpdate - Callback function called with available slots count: function(availableSlots, data)
   */
  fetchAndUpdate: function(lessonId, targetElement, options) {
    options = options || {};
    const host = options.host || window.location.host;
    // Use translations if available, otherwise fallback to defaults
    const translations = window.trainingTranslations || {};
    const loadingText = options.loadingText || translations.loading || 'Loading...';
    const errorText = options.errorText || translations.errorLoading || 'Error loading';
    const unavailableText = options.unavailableText || translations.notAvailable || 'Not available';
    const volzetText = options.volzetText || translations.volzet || 'Full';

    // Get target element
    let element;
    if (typeof targetElement === 'string') {
      element = document.getElementById(targetElement);
    } else {
      element = targetElement;
    }

    if (!element) {
      return Promise.reject(new Error('Target element not found'));
    }

    if (!lessonId) {
      element.textContent = unavailableText;
      return Promise.reject(new Error('Lesson ID not provided'));
    }

    // Show loading state
    element.textContent = loadingText;

    // Build proxy URL
    const proxyUrl = this.buildProxyUrl(lessonId, host);

    // Fetch lesson data from Laravel API via Shopify proxy
    return fetch(proxyUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      redirect: 'manual'
    })
      .then(response => {
        // Handle redirects (302, 301, etc.)
        if (response.status >= 300 && response.status < 400) {
          const location = response.headers.get('Location');
          throw new Error(`Request was redirected (${response.status}) to: ${location || 'unknown'}. Check your Shopify app proxy configuration for the lessons endpoint.`);
        }

        if (!response.ok) {
          // Try to get error message from response
          return response.text().then(text => {
            // Check if it's an HTML redirect page
            if (text.includes('<meta http-equiv="refresh"') || text.includes('Redirecting to')) {
              const redirectMatch = text.match(/url=['"]([^'"]+)['"]/);
              const redirectUrl = redirectMatch ? redirectMatch[1] : 'unknown';
              throw new Error(`Shopify app proxy is redirecting instead of forwarding. The proxy URL for lessons endpoint may be incorrect. Redirecting to: ${redirectUrl}`);
            }

            // Parse JSON error if possible
            let errorMessage = text;
            try {
              const errorJson = JSON.parse(text);
              if (errorJson.message) {
                errorMessage = errorJson.message;
              }
            } catch (e) {
              // Not JSON, use text as is
            }

            // Check for Cloudflare error 1002
            if (response.status === 403 || text.includes('1002')) {
              throw new Error('Proxy configuration error: The target domain must point to a publicly accessible IP address, not localhost.');
            }

            throw new Error(`HTTP ${response.status}: ${errorMessage || response.statusText}`);
          });
        }
        return response.json();
      })
      .then(data => {
        if (!data) {
          const translations = window.trainingTranslations || {};
          element.textContent = translations.noData || 'No data';
          return;
        }

        // Calculate and update available slots
        const availableSlots = this.calculateAvailableSlots(data);
        // Show "volzet" text instead of 0
        element.textContent = availableSlots === 0 ? volzetText : availableSlots;

        // Call callback if provided
        if (options.onUpdate && typeof options.onUpdate === 'function') {
          options.onUpdate(availableSlots, data);
        }

        return data;
      })
      .catch(error => {
        element.textContent = errorText;
        throw error;
      });
  }
};

